import { useMemo, useState } from "react"
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import axios from "axios"
import moment from "moment"

import { Pagination } from "components"
import Table from "components/table"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { useAuth } from "contexts/auth-context"

import useBranchRepositories from "repositories/branch"
import useStockOpnameListRepositories from "repositories/stock-opname"
import { extractApiErrorMessage, getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status" },
  { id: "ACTIVE", name: "Aktif (input cek)" },
  { id: "SUBMITTED", name: "Menunggu approval" },
  { id: "APPROVED", name: "Disetujui HO" },
  { id: "REJECTED", name: "Ditolak HO" },
  { id: "POSTED", name: "Diposting" },
  { id: "CANCELED", name: "Dibatalkan" },
]

const STATUS_BADGE = {
  ACTIVE: "bg-amber-50 text-amber-700 border-amber-200",
  SUBMITTED: "bg-blue-50 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
  POSTED: "bg-green-50 text-green-700 border-green-200",
  CANCELED: "bg-neutral-50 text-neutral-700 border-neutral-200",
}

export default function List() {
  const { isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")
  const [creating, setCreating] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newOutletId, setNewOutletId] = useState(
    !isSuper() && currentBranch?.id ? String(currentBranch.id) : ""
  )
  const [newNotes, setNewNotes] = useState("")

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
  const rawOutletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const outletId = isBranchLocked ? String(currentBranch.id) : rawOutletId
  // Grup → CSV id (mis. "1,15"); outlet individual → id tunggal.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const status = searchParams.get("status") || "ALL"
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""
  const page = searchParams.get("page") || "1"

  const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false })
  const createOutletOptions = useMemo(() => {
    return Array.isArray(branchSelection) ? branchSelection : []
  }, [branchSelection])

  const { data, isLoading, mutate } = useStockOpnameListRepositories({
    search: searchParams.get("search") || undefined,
    outlet_id: resolvedOutletCsv || undefined,
    status: status === "ALL" ? undefined : status,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
  })

  const rows = useMemo(() => data?.data ?? [], [data])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })
    setSearchParams(merged)
  }

  const submitSearch = () => {
    mergeParams({ search: searchText || null, page: 1 })
  }

  const createFreeze = async (e) => {
    e?.preventDefault?.()
    if (!newOutletId) {
      toast.error("Pilih outlet terlebih dahulu.")
      return
    }
    setCreating(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname`,
        { outlet_id: newOutletId, notes: newNotes || undefined }
      )
      const payload = res.data?.data || {}
      toast.success(res.data?.message || "Freeze stock berhasil dibuat")
      setShowCreate(false)
      setNewNotes("")
      await mutate()
      const id = payload.freeze?.id || payload.id
      if (id) navigate(`/stock-opname/${id}`)
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Gagal membuat freeze stock"))
    } finally {
      setCreating(false)
    }
  }

  const headers = [
    { isAction: false, content: "No Freeze" },
    { isAction: false, content: "Outlet" },
    { isAction: false, content: "Status" },
    { isAction: false, content: "Dibekukan Pada" },
    { isAction: false, content: "Diposting/Dibatalkan Pada" },
    { isAction: false, content: "Cek Stock" },
    { isAction: false, content: "Dibuat Oleh" },
    { isAction: false, content: "Catatan" },
    { isAction: true, content: "Aksi" },
  ]

  const columns = [
    { type: "default", bold: true, content: (v) => v.freeze_no || "-" },
    { type: "default", content: (v) => v.outlet_name || v.outlet_id || "-" },
    {
      type: "default",
      content: (v) => (
        <span
          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
            STATUS_BADGE[v.status] || STATUS_BADGE.CANCELED
          }`}
        >
          {v.status}
        </span>
      ),
    },
    { type: "default", content: (v) => (v.frozen_at ? moment(v.frozen_at).format("DD/MM/YYYY HH:mm") : "-") },
    {
      type: "default",
      content: (v) => {
        const ts = v.posted_at || v.canceled_at
        return ts ? moment(ts).format("DD/MM/YYYY HH:mm") : "-"
      },
    },
    { type: "default", content: (v) => v.checks_count || 0 },
    { type: "default", content: (v) => v.created_by || "-" },
    { type: "default", content: (v) => v.notes || "-" },
    {
      type: "action",
      content: (v) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/stock-opname/${v.id}`}
            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg"
          >
            {v.status === "ACTIVE" ? "Buka" : "Lihat"}
          </Link>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Stock Opname</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Freeze stock outlet, lakukan beberapa cek fisik, lalu cek selisih dan posting.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center px-4 py-3 text-sm font-medium text-white bg-brand-900 rounded-xl"
          >
            {showCreate ? "Tutup" : "Freeze Stock Baru"}
          </button>
        </div>

        {showCreate && (
          <form
            onSubmit={createFreeze}
            className="p-4 mt-4 border rounded-xl border-neutral-200 bg-neutral-50"
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Label value="Outlet *" />
                <SelectDescription
                  selection={createOutletOptions}
                  isLoading={isLoadingBranches}
                  value={newOutletId}
                  onChange={(value) => setNewOutletId(value || "")}
                  keyValue={(v) => v.id}
                  title={(v) => v.name}
                  description={(v) => v.code || v.id}
                  placeholder="Pilih outlet untuk freeze..."
                  disabled={isBranchLocked}
                />
              </div>
              <div className="md:col-span-2">
                <Label value="Catatan (opsional)" />
                <Input
                  type="text"
                  value={newNotes}
                  placeholder="Misal: Stock opname akhir bulan Mei"
                  onChange={(e) => setNewNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="submit"
                disabled={creating || !newOutletId}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {creating ? "Memproses..." : "Freeze Stock Outlet"}
              </button>
              <p className="text-xs text-neutral-500">
                Saat freeze aktif: POS &amp; transaksi lain tetap jalan tapi actual stock tidak berubah sampai posting/cancel.
              </p>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-5">
          <div>
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeParams({ outlet_id: v, page: 1 })}
              disabled={isBranchLocked}
              className="w-full mt-1 p-2 text-sm transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />
          </div>
          <div>
            <Label value="Status" />
            <SelectDescription
              selection={STATUS_OPTIONS}
              value={status}
              onChange={(value) => mergeParams({ status: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Pilih Status..."
            />
          </div>
          <div>
            <Label value="Tanggal Dari" />
            <Input type="date" value={dateFrom} onChange={(e) => mergeParams({ date_from: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Tanggal Sampai" />
            <Input type="date" value={dateTo} onChange={(e) => mergeParams({ date_to: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Cari" />
            <Input
              type="text"
              value={searchText}
              placeholder="No freeze / outlet..."
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <Table isLoading={isLoading} data={rows} headers={headers} columns={columns} />
        <Pagination links={data?.links} from={data?.from} to={data?.to} total={data?.total} />
      </div>
    </div>
  )
}
